import { useState } from 'react';
import { Users, Plus, Bell, Settings, Bookmark } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import PostList from '@/components/community/PostList';
import PostForm from '@/components/community/PostForm';
import NotificationBell from '@/components/community/NotificationBell';
import CommunitiesList from '@/components/community/CommunitiesList';
import { postsAPI } from '@/api/community';
import { getUserData } from '@/utils/auth';

export default function TeacherCommunity() {
  const [activeTab, setActiveTab] = useState('announcements');
  const [showPostForm, setShowPostForm] = useState(false);
  const [showCommunities, setShowCommunities] = useState(false);
  const [showSavedPosts, setShowSavedPosts] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const userData = getUserData('teacher');
  const userType = userData?.user_type || 'teacher';

  const handlePostCreated = () => {
    setRefreshKey(prev => prev + 1);
    setShowPostForm(false);
  };

  const handlePostUpdate = () => {
    setRefreshKey(prev => prev + 1);
  };

  // Fetch functions for each tab
  const fetchAnnouncements = (page, pageSize) => {
    return postsAPI.getAnnouncements(page, pageSize);
  };

  const fetchMyFeed = (page, pageSize) => {
    return postsAPI.getMyFeed(page, pageSize);
  };

  const fetchAllPosts = (page, pageSize) => {
    return postsAPI.getAllPosts(page, pageSize);
  };

  const fetchSavedPosts = (page, pageSize) => {
    return postsAPI.getSavedPosts(page, pageSize);
  };

  return (
    <div className="flex flex-col h-full bg-[hsl(var(--main-bg))] p-6 overflow-y-auto">
      <div className="max-w-4xl mx-auto w-full space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Users className="w-8 h-8 text-[hsl(var(--green-bright))]" />
            <div>
              <h1 className="text-3xl font-bold text-foreground">Community</h1>
              <p className="text-muted-foreground">Connect, share, and engage with your school community</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <Button
              variant="outline"
              onClick={() => setShowCommunities(true)}
            >
              <Settings className="w-4 h-4 mr-2" />
              Manage Communities
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowSavedPosts(true)}
              className="text-[hsl(var(--green-bright))] hover:text-[hsl(var(--teal-vivid))]"
            >
              <Bookmark className="w-4 h-4 mr-2" />
              Saved Posts
            </Button>
            <Button
              onClick={() => setShowPostForm(true)}
              className="bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] text-white hover:from-[hsl(var(--teal-vivid))] hover:to-[hsl(var(--green-bright))]"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Post
            </Button>
          </div>
        </div>

        {/* Three Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="announcements">Announcements</TabsTrigger>
            <TabsTrigger value="my-feed">My Feed</TabsTrigger>
            <TabsTrigger value="posts">Posts</TabsTrigger>
          </TabsList>

          <TabsContent value="announcements" className="mt-4">
            <PostList
              key={`announcements-${refreshKey}`}
              fetchFunction={fetchAnnouncements}
              onPostUpdate={handlePostUpdate}
              isGuardian={false}
            />
          </TabsContent>

          <TabsContent value="my-feed" className="mt-4">
            <PostList
              key={`my-feed-${refreshKey}`}
              fetchFunction={fetchMyFeed}
              onPostUpdate={handlePostUpdate}
              isGuardian={false}
            />
          </TabsContent>

          <TabsContent value="posts" className="mt-4">
            <PostList
              key={`posts-${refreshKey}`}
              fetchFunction={fetchAllPosts}
              onPostUpdate={handlePostUpdate}
              isGuardian={false}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Post Form Modal */}
      <PostForm
        isOpen={showPostForm}
        onClose={() => setShowPostForm(false)}
        onPostCreated={handlePostCreated}
        userType={userType}
      />

      {/* Communities Management Modal */}
      {showCommunities && (
        <CommunitiesList
          isOpen={showCommunities}
          onClose={() => setShowCommunities(false)}
          isAdmin={false}
          isTeacher={false}
        />
      )}

      {/* Saved Posts Modal */}
      {showSavedPosts && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowSavedPosts(false)}>
          <div className="bg-[hsl(var(--card-bg))] rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-[hsl(var(--card-border))]">
              <h2 className="text-2xl font-bold text-foreground">Saved Posts</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowSavedPosts(false)}
              >
                ×
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <PostList
                key={`saved-posts-${refreshKey}`}
                fetchFunction={fetchSavedPosts}
                onPostUpdate={handlePostUpdate}
                isGuardian={false}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}